import React from 'react';

const FolderRow = ({
    folder, level, activeFolderId, setActiveFolderId,
    onRequestModal, onDeleteFolder, handleDrop,
    hoveredFolderId, setHoveredFolderId,
    expandedFolders, toggleFolder
}) => {
    const isExpanded = expandedFolders.has(folder.id);
    const hasChildren = folder.children && folder.children.length > 0;

    return (
        <div className="flex flex-col">
            <div
                className={`flex items-center gap-1 group transition-all duration-200 py-0.5 px-1 rounded-md ${activeFolderId === folder.id ? 'bg-blue-600/20 text-blue-400' : 'hover:bg-white/5'}`}
                style={{ paddingLeft: `${level * 16}px` }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setHoveredFolderId(folder.id); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setHoveredFolderId(null); }}
                onDrop={(e) => { setHoveredFolderId(null); handleDrop(e, folder.id); }}
            >
                <div className="flex items-center gap-1 flex-1">
                    <button
                        className={`w-4 h-4 flex items-center justify-center text-[10px] transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''} ${!hasChildren ? 'opacity-0' : 'text-gray-500 hover:text-white'}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleFolder(folder.id);
                        }}
                    >
                        ▶
                    </button>
                    <button
                        className={`flex-1 text-left text-xs font-medium truncate py-1.5 transition-colors ${activeFolderId === folder.id ? 'text-blue-400' : 'text-gray-300 hover:text-white'}`}
                        onClick={() => setActiveFolderId(folder.id)}
                    >
                        {isExpanded && hasChildren ? '📂' : '📁'} {folder.name}
                    </button>
                </div>

                <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        className="w-6 h-6 flex items-center justify-center text-xs text-gray-500 hover:text-blue-400 transition-colors"
                        onClick={() => onRequestModal('create-folder', folder.id)}
                        title="Add Subfolder"
                    >
                        <span className="scale-125">+</span>
                    </button>
                    <button
                        className="w-6 h-6 flex items-center justify-center text-[10px] text-gray-500 hover:text-white transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRequestModal('rename-folder', folder);
                        }}
                        title="Rename Folder"
                    >
                        ✎
                    </button>
                    <button
                        className="w-6 h-6 flex items-center justify-center text-[10px] text-gray-500 hover:text-red-500 transition-all active:scale-90"
                        onClick={() => {
                            if (window.confirm(`Delete folder "${folder.name}"?`)) onDeleteFolder(folder.id);
                        }}
                    >
                        🗑️
                    </button>
                </div>
            </div>
            {isExpanded && hasChildren && (
                <div className="flex flex-col">
                    {folder.children.map(child => (
                        <FolderRow
                            key={child.id}
                            folder={child}
                            level={level + 1}
                            activeFolderId={activeFolderId}
                            setActiveFolderId={setActiveFolderId}
                            onRequestModal={onRequestModal}
                            onDeleteFolder={onDeleteFolder}
                            handleDrop={handleDrop}
                            hoveredFolderId={hoveredFolderId}
                            setHoveredFolderId={setHoveredFolderId}
                            expandedFolders={expandedFolders}
                            toggleFolder={toggleFolder}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default function QuickRoutesPanel({
    teamType, setTeamType, availableTypes,
    formations,
    filterType, setFilterType,
    filterFormation, setFilterFormation,
    onRequestModal,
    // Add Folder Props
    playbook,
    activeFolderId, setActiveFolderId,
    onCreateFolder, onDeleteFolder, onAssignPlays
}) {
    const [hoveredFolderId, setHoveredFolderId] = React.useState(null);
    const [expandedFolders, setExpandedFolders] = React.useState(new Set());
    const [folderSortBy, setFolderSortBy] = React.useState('alpha'); // 'alpha', 'newest', 'updated'

    const toggleFolder = (id) => {
        setExpandedFolders(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const folderTree = React.useMemo(() => {
        const list = Object.values(playbook?.folders || {});
        const map = {};
        const roots = [];
        list.forEach(f => {
            map[f.id] = { ...f, children: [] };
        });
        list.forEach(f => {
            if (f.parentId && map[f.parentId]) {
                map[f.parentId].children.push(map[f.id]);
            } else {
                roots.push(map[f.id]);
            }
        });

        // Helper to get timestamp from folder ID (folder_123456789)
        const getTimestamp = (f) => {
            if (f.createdAt) return new Date(f.createdAt).getTime();
            const match = f.id.match(/\d+/);
            return match ? parseInt(match[0]) : 0;
        };

        const getUpdated = (f) => {
            if (f.updatedAt) return new Date(f.updatedAt).getTime();
            return getTimestamp(f);
        };

        // Recursive sort
        const sortNodes = (nodes) => {
            nodes.sort((a, b) => {
                if (folderSortBy === 'newest') {
                    return getTimestamp(b) - getTimestamp(a);
                }
                if (folderSortBy === 'updated') {
                    return getUpdated(b) - getUpdated(a);
                }
                // Default: Alphabetical
                return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
            });
            nodes.forEach(node => {
                if (node.children) sortNodes(node.children);
            });
        };
        sortNodes(roots);

        return roots;
    }, [playbook?.folders, folderSortBy]);

    const toggle = (set, value) => {
        const next = new Set(set);
        next.has(value) ? next.delete(value) : next.add(value);
        return next;
    };

    const clearFilters = () => {
        setFilterType(new Set());
        setFilterFormation(new Set());
        setActiveFolderId(null);
    };

    const handleDrop = (e, folderId) => {
        e.preventDefault();
        const data = e.dataTransfer.getData("application/json");
        if (!data) return;
        try {
            const { names } = JSON.parse(data);
            onAssignPlays(names, folderId);
        } catch (err) {
            console.error("Drop failed", err);
        }
    };

    return (
        <div className="qr-rail overflow-y-auto max-h-full scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
            {/* Type Selection (Primary Scope) */}
            <div className="qr-group">
                <div className="flex justify-between items-center mb-3">
                    <div className="qr-label text-[10px] uppercase tracking-wider font-bold text-gray-500">Category</div>
                    <button
                        className="text-[10px] text-blue-400 hover:text-white border border-blue-900 bg-blue-900/40 rounded px-2 py-0.5 transition-all active:scale-95"
                        onClick={() => onRequestModal('create-type')}
                    >+ New</button>
                </div>
                <div className="flex flex-col gap-1">
                    {availableTypes.map(t => (
                        <div key={t} className="flex items-center gap-1 group">
                            <button
                                className={`qr-chip flex-1 text-xs text-left px-3 py-2 rounded-md transition-all duration-200 ${teamType === t ? 'active bg-blue-600 text-white shadow-lg' : 'hover:bg-white/5 text-gray-400 hover:text-white'}`}
                                onClick={() => {
                                    setTeamType(t);
                                    setActiveFolderId(null);
                                }}
                            >{t}</button>
                            <button
                                className="text-[10px] text-gray-500 hover:text-white px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRequestModal('rename-type', t);
                                }}
                                title="Rename Category"
                            >✎</button>
                        </div>
                    ))}
                </div>
            </div>

            <button
                className="text-xs text-blue-400 hover:text-blue-300 border border-blue-900/30 bg-blue-900/10 hover:bg-blue-900/20 rounded-md px-2 py-2.5 mt-4 mb-4 w-full text-center font-bold tracking-tight transition-all active:scale-[0.98]"
                onClick={clearFilters}
            >
                Clear All Filter & Folders
            </button>

            {/* Folders Section */}
            <div className="qr-group mt-2">
                <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                        <div className="qr-label text-[10px] uppercase tracking-wider font-bold text-gray-500">Folders</div>
                        <select
                            className="bg-transparent text-[9px] text-gray-500 hover:text-blue-400 outline-none cursor-pointer font-bold uppercase tracking-tighter transition-colors"
                            value={folderSortBy}
                            onChange={(e) => setFolderSortBy(e.target.value)}
                        >
                            <option value="alpha" className="bg-gray-900">A-Z</option>
                            <option value="newest" className="bg-gray-900">Newest</option>
                            <option value="updated" className="bg-gray-900">Recent</option>
                        </select>
                    </div>
                    <button
                        className="text-[10px] text-blue-400 hover:text-white border border-blue-900 bg-blue-900/40 rounded px-2 py-0.5 transition-all active:scale-95"
                        onClick={() => onRequestModal('create-folder')}
                    >+ New</button>
                </div>
                <div className="flex flex-col gap-0.5">
                    <div className="flex flex-col">
                        <div
                            className="flex items-center gap-1 group transition-all duration-200 py-0.5 px-1 rounded-md hover:bg-white/5"
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setHoveredFolderId('root'); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setHoveredFolderId(null); }}
                            onDrop={(e) => { setHoveredFolderId(null); handleDrop(e, null); }}
                        >
                            <div className="w-4 h-4 opacity-0" />
                            <button
                                className={`flex-1 text-left text-xs font-medium py-1.5 transition-colors ${!activeFolderId ? 'text-blue-400' : 'text-gray-300 hover:text-white'}`}
                                onClick={() => setActiveFolderId(null)}
                            >
                                📂 All Plays
                            </button>
                        </div>
                    </div>
                    {folderTree.map(f => (
                        <FolderRow
                            key={f.id}
                            folder={f}
                            level={0}
                            activeFolderId={activeFolderId}
                            setActiveFolderId={setActiveFolderId}
                            onRequestModal={onRequestModal}
                            onDeleteFolder={onDeleteFolder}
                            handleDrop={handleDrop}
                            hoveredFolderId={hoveredFolderId}
                            setHoveredFolderId={setHoveredFolderId}
                            expandedFolders={expandedFolders}
                            toggleFolder={toggleFolder}
                        />
                    ))}
                </div>
            </div>

            {/* Filter: Formation */}
            <div className="qr-group mt-2">
                <div className="flex justify-between items-center mb-2">
                    <div className="qr-label">Formation Filter</div>
                    <button
                        className="text-xs text-blue-400 hover:text-white border border-blue-900 bg-blue-900/40 rounded px-2 py-0.5"
                        onClick={() => onRequestModal('create-formation')}
                    >+ New</button>
                </div>

                {formations.length === 0 ? <div className="qr-empty text-xs">No formations</div> :
                    formations.map(f => (
                        <div key={f} className="flex items-center gap-1 group">
                            <button
                                className={`qr-chip flex-1 ${filterFormation.has(f) ? 'active' : ''}`}
                                onClick={() => setFilterFormation(prev => toggle(prev, f))}
                            >{f}</button>
                            <button
                                className="text-[12px] text-gray-400 hover:text-white px-2 py-1"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRequestModal('rename-formation', f);
                                }}
                                title="Rename Formation"
                            >✎</button>
                        </div>
                    ))
                }
            </div>
        </div>
    );
}
